/* Image-free presentation and linked-color loading only. Symmetry owns
   product content conversion, variant selection, submission and cart updates. */
(() => {
  if (theme.lunyaQuickbuy) return;
  const enabled = () => document.body.matches('.lunya-brand:is(.template-collection, .template-index)');
  const isColour = selector => /^(color|colour)$/i.test(selector.dataset.option || '');
  let pending = null;

  const selections = form => [...form.querySelectorAll('variant-picker .option-selector')]
    .filter(selector => !isColour(selector))
    .map(selector => ({
      name: selector.dataset.option,
      value: selector.querySelector('input:checked')?.value
        || selector.querySelector('.js-option[aria-selected="true"]')?.dataset.value || null
    }));

  const clearSelection = content => {
    content.querySelectorAll('variant-picker .option-selector').forEach(selector => {
      if (isColour(selector)) {
        // A hidden single-color option must stay selected for native requests.
        const inputs = selector.querySelectorAll('input.js-option');
        if (inputs.length === 1) {
          inputs[0].checked = true;
          inputs[0].setAttribute('checked', '');
        }
        const options = selector.querySelectorAll('.js-option:not(input, [data-value=""])');
        if (!inputs.length && options.length === 1) options[0].setAttribute('aria-selected', 'true');
        return;
      }
      selector.querySelectorAll('input').forEach(input => {
        input.checked = false;
        input.removeAttribute('checked');
      });
      selector.querySelectorAll('.js-option[aria-selected]').forEach(option => option.setAttribute('aria-selected', 'false'));
      selector.querySelectorAll('.lunya-option-legend__value, .lunya-option-legend__stock').forEach(label => label.remove());
      const select = selector.querySelector('custom-select');
      if (select) {
        const list = select.querySelector('.custom-select__listbox');
        const prompt = `${theme.strings.productsProductChooseA} ${selector.dataset.option}`;
        let placeholder = list.querySelector('.js-option[data-value=""]');
        if (!placeholder) {
          placeholder = document.createElement('li');
          placeholder.className = 'custom-select__option js-option';
          placeholder.id = `${select.id}-opt-default`;
          placeholder.dataset.value = '';
          placeholder.setAttribute('role', 'option');
          list.prepend(placeholder);
        }
        placeholder.textContent = prompt;
        placeholder.setAttribute('aria-selected', 'true');
        list.setAttribute('aria-activedescendant', placeholder.id);
        select.querySelector('.custom-select__btn .text-start').textContent = prompt;
      }
    });
    content.querySelectorAll('variant-picker script[type="application/json"]').forEach(script => script.remove());
    content.querySelectorAll('form input[name="id"]').forEach(id => {
      id.value = '';
      id.setAttribute('value', '');
    });
    const add = content.querySelector('[name="add"]');
    if (add) {
      const option = [...content.querySelectorAll('variant-picker .option-selector')].find(selector => !isColour(selector));
      add.dataset.preselectionText = `${theme.strings.productsProductChooseA} ${option?.dataset.option || ''}`.trim();
      add.dataset.preselectionDisabled = 'true';
      add.textContent = add.dataset.preselectionText;
      add.disabled = true;
    }
  };

  const preserveSelection = async (tmpl, productUrl, previous, signal) => {
    const content = tmpl.content.querySelector('.quickbuy-content');
    if (!content) throw new Error('Quick Buy product form missing');
    const selectors = [...content.querySelectorAll('variant-picker .option-selector')];
    const optionIds = [];
    const requestedValues = [];
    let complete = true; // Color-only products have no size to preserve.
    selectors.forEach(selector => {
      const colourOptions = selector.querySelectorAll('.js-option:not([data-value=""])');
      const value = isColour(selector)
        ? selector.querySelector('input:checked')?.value
          || selector.querySelector('.js-option[aria-selected="true"]')?.dataset.value
          || (colourOptions.length === 1 ? colourOptions[0].value || colourOptions[0].dataset.value : null)
        : previous.find(entry => entry.name === selector.dataset.option)?.value;
      const input = [...selector.querySelectorAll('.js-option')]
        .find(option => (option.value || option.dataset.value) === value);
      if (!input?.dataset.valueId) complete = false;
      optionIds.push(input?.dataset.valueId);
      requestedValues.push(value);
    });
    if (!selectors.length) return;
    if (!complete) { clearSelection(content); return; }
    const matchesRequested = element => {
      const data = element.querySelector('variant-picker script[type="application/json"]');
      const variant = data ? JSON.parse(data.textContent) : null;
      return variant?.available && variant.options?.length === requestedValues.length
        && requestedValues.every((value, index) => variant.options[index] === value);
    };
    // The initial response already resolves color-only products completely.
    if (selectors.every(isColour) && matchesRequested(content)) return;
    // Option-value IDs belong to the new product, not the outgoing colorway.
    const url = new URL(productUrl);
    url.searchParams.delete('variant');
    url.searchParams.set('option_values', optionIds.join(','));
    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
    const selected = document.createElement('template');
    selected.innerHTML = await response.text();
    const selectedContent = selected.content.querySelector('.quickbuy-content');
    if (!selectedContent) throw new Error('Quick Buy selected form missing');
    if (!matchesRequested(selectedContent)) clearSelection(selectedContent);
    tmpl.content.replaceChildren(selected.content);
  };

  const release = (state, recoverOpen = true) => {
    // An invalidated native request may have checked a size without committing
    // its variant ID. Reinitialize that form without a selection on recovery.
    if (state.recovery && state.form.isConnected) {
      state.form.replaceWith(state.recovery);
      if (recoverOpen) state.container.dispatchEvent(new CustomEvent('on:quickbuy:after-open', { bubbles: true }));
      return;
    }
    state.form.removeAttribute('aria-busy');
    state.form.querySelectorAll('variant-picker, .js-product-form').forEach(element => { element.inert = false; });
    if (state.add) state.add.disabled = state.disabled;
  };
  const cancel = (recoverOpen = false) => {
    if (!pending) return;
    pending.controller.abort();
    release(pending, recoverOpen === true);
    pending.status.remove();
    pending = null;
  };

  theme.lunyaQuickbuy = {
    prepare: content => {
      if (!enabled()) return;
      if (content.matches('.quickbuy-content')) content.classList.add('lunya-quickbuy-content');
      content.querySelectorAll('media-gallery, .media-gallery, .product-media-collage').forEach(gallery => gallery.remove());
      content.querySelectorAll('.lunya-colourways-block').forEach(block => block.classList.remove('not-in-quickbuy'));
      content.querySelectorAll('.lunya-colourways a.lunya-swatch').forEach(link => link.setAttribute('data-lunya-quickbuy-color', ''));
      content.querySelectorAll('.lunya-swatch[aria-current]').forEach(swatch => swatch.setAttribute('tabindex', '-1'));
    }
  };

  document.addEventListener('on:quickbuy:before-open', evt => {
    cancel();
    evt.target.querySelectorAll('.lunya-quickbuy-status').forEach(status => status.remove());
    if (enabled()) evt.target.classList.add('lunya-quickbuy');
  });
  document.addEventListener('on:quickbuy:after-close', evt => {
    if (pending?.container === evt.target) cancel();
    evt.target.querySelectorAll('.lunya-quickbuy-status').forEach(status => status.remove());
    evt.target.classList.remove('lunya-quickbuy');
  });
  document.addEventListener('collection:before-replace', cancel);
  document.addEventListener('shopify:section:unload', evt => {
    if (pending && evt.target.contains(pending.container)) cancel();
  });
  window.addEventListener('pagehide', cancel);

  document.addEventListener('click', async evt => {
    const link = evt.target.closest('[data-lunya-quickbuy-color]');
    const container = link?.closest('.quickbuy-container.lunya-quickbuy');
    if (!enabled() || !container || evt.button !== 0 || evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey) return;
    const block = container.closest('.product-block')
      || container.closest('.collection-slider-row')?.querySelector('.product-block.expanded');
    let form = container.querySelector('product-form');
    if (!block || !form) return;
    const url = new URL(link.href);
    if (url.origin !== new URL(container.dataset.productUrl).origin) return;
    evt.preventDefault();
    // Never replace a form whose native cart submission is still pending.
    if (form.querySelector('[name="add"][aria-disabled="true"]')) return;
    const previous = pending?.container === container ? pending.previous : selections(form);
    cancel(pending?.container === container);
    form = container.querySelector('product-form');
    container.querySelectorAll('.lunya-quickbuy-status').forEach(status => status.remove());
    theme.quickBuy.currentRequestAbortController?.abort();
    const controller = new AbortController();
    theme.quickBuy.currentRequestAbortController = controller;
    const status = document.createElement('p');
    status.className = 'lunya-quickbuy-status';
    status.setAttribute('role', 'status');
    status.textContent = theme.strings.quickbuyLoading;
    container.querySelector('.inner').appendChild(status);
    const add = form.querySelector('[name="add"]');
    const uncommitted = [...form.querySelectorAll('variant-picker')].some(picker =>
      picker.getSelectedOptions().some((value, index) => value !== (picker.variant?.options?.[index] ?? null)));
    // Parse in an inert template so native custom elements initialize only
    // after their replacement is back inside the Quick Buy container.
    const recoveryTemplate = document.createElement('template');
    if (uncommitted) recoveryTemplate.innerHTML = form.outerHTML;
    const recovery = recoveryTemplate.content.firstElementChild;
    if (recovery) {
      // outerHTML does not serialize dirty radio/checkbox selection properties.
      const originalInputs = [...form.querySelectorAll('input')];
      recovery.querySelectorAll('input').forEach((input, index) => {
        if (input.matches('[type="radio"], [type="checkbox"]')) {
          input.toggleAttribute('checked', originalInputs[index].checked);
        }
      });
      clearSelection(recovery);
    }
    const state = { container, form, controller, status, add, disabled: add?.disabled, previous, recovery };
    pending = state;
    form.setAttribute('aria-busy', 'true');
    form.querySelectorAll('variant-picker').forEach(picker => {
      picker.variantRequestId += 1; // Discard outgoing native variant responses.
      picker.inert = true;
    });
    form.querySelectorAll('.js-product-form').forEach(element => { element.inert = true; });
    if (add) add.disabled = true;
    try {
      const loaded = await theme.quickBuy.loadContent(block, container, url.href, controller,
        (tmpl, signal) => preserveSelection(tmpl, url.href, previous, signal));
      if (pending !== state) return;
      pending = null;
      release(state);
      status.remove();
      if (loaded) {
        (container.querySelector('.lunya-swatch[aria-current]') || container.querySelector('.close-detail'))?.focus({ preventScroll: true });
      }
    } catch (error) {
      if (controller.signal.aborted || pending !== state) return;
      pending = null;
      release(state);
      status.setAttribute('role', 'alert');
      status.textContent = '';
      const fallback = document.createElement('a');
      fallback.href = url.href;
      fallback.textContent = theme.strings.quickbuyError;
      status.appendChild(fallback);
      [...container.querySelectorAll('[data-lunya-quickbuy-color]')]
        .find(swatch => swatch.href === url.href)?.focus({ preventScroll: true });
    }
  });
})();
